import { useCallback, useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { FormProvider, useNotificationContext } from 'core/states';
import { useElementTemplateActions } from 'elements/hooks';
import { IAddElementTemplateToJobFormData, IElementTemplate } from 'elements/types';
import { ProjectFormField } from 'projects/components';

import { mapAddElementTemplateToJobData } from './mappers';
import { addElementTemplateToJobSchema } from './schema';

interface IProps {
  elementTemplate: IElementTemplate;
  isOpen?: boolean;
  onClose?: () => void;
}

export const AddElementTemplateToJob = ({ elementTemplate, isOpen, onClose }: IProps) => {
  const { t } = useTranslation();

  const { clearNotification } = useNotificationContext();

  const { addElementTemplateToJob } = useElementTemplateActions();

  const defaultValues = useMemo(
    () => ({
      project: null,
      jobName: '',
      name: `${elementTemplate.customIdentifier} | ${elementTemplate.description}`,
      quantity: 1,
    }),
    [elementTemplate]
  );

  const handleOnClose = useCallback(
    (reset: UseFormReset<IAddElementTemplateToJobFormData>) => {
      reset();
      clearNotification();
      onClose?.();
    },
    [clearNotification, onClose]
  );

  const onSubmit = useCallback(
    (reset: UseFormReset<IAddElementTemplateToJobFormData>) =>
      async (data: IAddElementTemplateToJobFormData) => {
        const { success } = await addElementTemplateToJob(
          mapAddElementTemplateToJobData(data, elementTemplate.id)
        );

        if (!success) {
          return;
        }

        setTimeout(() => handleOnClose(reset), 1000);
      },
    [addElementTemplateToJob, elementTemplate, handleOnClose]
  );

  return (
    <FormProvider
      schema={addElementTemplateToJobSchema()}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <Form
        title={t('titleAddElementTemplateToJob')}
        subtitle={t('subtitleAddElementTemplateToJob', {
          template: elementTemplate.customIdentifier,
        })}
        isOpen={isOpen}
        onClose={handleOnClose}
        submitBtnLabel={t('buttonLabelAddToJob')}
        onSubmit={onSubmit}
      >
        <ProjectFormField />

        <FormField
          name="jobName"
          type={InputType.TEXT_INPUT}
          inputProps={{
            label: t('labelJobName'),
            required: true,
          }}
        />

        <FormField
          name="name"
          type={InputType.TEXT_INPUT}
          inputProps={{
            label: t('labelElementName'),
            required: true,
          }}
        />

        <FormField
          name="quantity"
          type={InputType.NUMBER_INPUT}
          inputProps={{
            label: t('labelQuantity'),
            required: true,
          }}
        />
      </Form>
    </FormProvider>
  );
};
